import {
    type ComponentPropsWithRef,
    type ReactNode,
    useEffect,
    useRef,
} from "react";
import { pluralize } from "@common/helpers/string";
import {
    PresenceStack,
    type PresenceStackMember,
} from "@components/avatar/PresenceStack";
import {
    TaskAddFieldChip,
    type TaskAddFieldState,
} from "@components/tasks/board/TaskAddFieldChip";
import { TASK_CARD_CHIP_SPECS } from "@components/tasks/board/TaskBoardCardChips";
import {
    TaskChipButton,
    taskFieldProps,
} from "@components/tasks/board/TaskChipButton";
import {
    TASK_CARD_FIELD_LABELS,
    type TaskCardEditField,
} from "@components/tasks/board/taskCardEdits";
import { getDueDateLateness } from "@components/tasks/board/TaskBoardCardFields";
import { TaskStatusPill } from "@components/tasks/board/TaskStatusPill";
import { TASK_PRIORITY_CONFIG } from "@components/tasks/TaskPrioritySelect/taskPriority.constant";
import { TASK_STATUS_CONFIG } from "@components/tasks/TaskStatusSelect/taskStatus.constants";
import { formatDateOnly } from "@helpers/date";
import type { BoardCardField } from "@design-system/Board/Board.types";
import { hasFieldContent } from "@design-system/Board/BoardCardLayout";
import type { PickerTriggerElement } from "@design-system/Picker/Picker.lib";
import type { DemoEntity, DemoTask } from "./fixtures/demoTasks";
import { getAxisValueIds } from "./taskBoardAxes";
import {
    TASK_BOARD_FIELDS,
    type TaskBoardChange,
    type TaskBoardField,
} from "./taskBoardActions";
import {
    TaskDueDatePicker,
    TaskEntitiesPicker,
    TaskFieldPicker,
} from "./taskBoardPickers";
import chipStyles from "@components/tasks/board/TaskBoardChips.module.scss";
import cardStyles from "@components/tasks/board/TaskBoardCard.module.scss";

/** A real card chip's field that the playground's pickers edit. */
type ChipField = Extract<TaskCardEditField, TaskBoardField>;

const isChipField = (field: TaskCardEditField): field is ChipField =>
    TASK_BOARD_FIELDS.some((boardField) => boardField === field);

interface ChipArgs {
    task: DemoTask;
    onChange: (change: TaskBoardChange) => void;
}

/** The playground's picker for `field`, opened from `trigger`. */
const TaskCardFieldPicker = ({
    field,
    task,
    onChange,
    trigger,
    onOpenChange,
}: ChipArgs & {
    field: Exclude<TaskBoardField, "title">;
    trigger: PickerTriggerElement;
    onOpenChange?: (isOpen: boolean) => void;
}) => {
    if (field === "entities") {
        return (
            <TaskEntitiesPicker
                entityIds={task.entityIds}
                onChange={onChange}
                onOpenChange={onOpenChange}
                trigger={trigger}
            />
        );
    }
    if (field === "dueDate") {
        return (
            <TaskDueDatePicker
                dueDateOnly={task.dueDateOnly}
                onChange={onChange}
                onOpenChange={onOpenChange}
                trigger={trigger}
            />
        );
    }
    return (
        <TaskFieldPicker
            field={field}
            valueId={getAxisValueIds(task, field)[0]}
            onChange={onChange}
            onOpenChange={onOpenChange}
            trigger={trigger}
        />
    );
};

/**
 * Opens a field's picker from the Add field menu, as the real card's
 * shortcut anchor does: an unseen trigger over the chip, clicked once on
 * mount, ending once the picker closes.
 */
const TaskAddFieldAnchor = ({
    field,
    onEnd,
    ...args
}: ChipArgs & { field: ChipField; onEnd: () => void }) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const hasClickedRef = useRef(false);
    useEffect(() => {
        if (!hasClickedRef.current) {
            hasClickedRef.current = true;
            triggerRef.current?.click();
        }
    }, []);
    return (
        <span className={chipStyles.shortcutAnchor}>
            <TaskCardFieldPicker
                {...args}
                field={field}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        onEnd();
                    }
                }}
                trigger={
                    <TaskChipButton
                        ref={triggerRef}
                        aria-label={TASK_CARD_FIELD_LABELS[field]}
                        className={chipStyles.shortcutTrigger}
                    />
                }
            />
        </span>
    );
};

const getChipLabel = ({
    field,
    task,
    entities,
}: {
    field: ChipField;
    task: DemoTask;
    entities: readonly DemoEntity[];
}) => {
    if (field === "entities") {
        return `${pluralize("Entity", entities.length)}: ${entities.map(({ name }) => name).join(", ")}`;
    }
    if (field === "dueDate") {
        if (task.dueDateOnly === null) {
            return "Due date: ";
        }
        const lateness = getDueDateLateness({
            dueDate: task.dueDateOnly,
            status: task.status,
        });
        return [`Due date: ${formatDateOnly(task.dueDateOnly)}`, lateness]
            .filter(Boolean)
            .join(", ");
    }
    return `Priority: ${task.priority === null ? "" : TASK_PRIORITY_CONFIG[task.priority].label}`;
};

/**
 * The card's fields with each editable one that has a value as a chip that
 * opens its picker, then the real Add field chip for the editable fields
 * with none. Fields not in `editableFields` stay read only.
 */
export const getEditableTaskCardFields = ({
    fields,
    entities,
    editableFields,
    adding,
    ...args
}: ChipArgs & {
    fields: readonly BoardCardField[];
    entities: readonly DemoEntity[];
    editableFields: ReadonlySet<TaskBoardField>;
    adding: TaskAddFieldState;
}): BoardCardField[] => {
    const emptyFields: ChipField[] = [];
    const chipFields = fields.map((cardField) => {
        const spec = TASK_CARD_CHIP_SPECS.get(cardField.id);
        if (spec === undefined) {
            return cardField;
        }
        const { field, look } = spec;
        if (!isChipField(field) || !editableFields.has(field)) {
            return cardField;
        }
        if (!hasFieldContent(cardField.content)) {
            emptyFields.push(field);
            return cardField;
        }
        const label = getChipLabel({ field, entities, ...args });
        return {
            ...cardField,
            content: (
                <TaskCardFieldPicker
                    {...args}
                    field={field}
                    trigger={
                        <TaskChipButton
                            field={field}
                            look={look}
                            aria-label={label}
                        >
                            {cardField.content}
                        </TaskChipButton>
                    }
                />
            ),
        };
    });
    return [
        ...chipFields,
        {
            id: "addField",
            placement: "inlineEnd",
            content: (emptyFields.length > 0 || adding.field !== null) && (
                <TaskAddFieldChip
                    emptyFields={emptyFields}
                    renderControl={(field, onEnd) =>
                        isChipField(field) && (
                            <TaskAddFieldAnchor
                                {...args}
                                field={field}
                                onEnd={onEnd}
                            />
                        )
                    }
                    adding={adding}
                />
            ),
        },
    ];
};

/** The status pill as the card's first tag; with `onChange`, it opens the Status picker. */
export const TaskStatusChip = ({
    task,
    onChange,
}: Pick<ChipArgs, "task"> & Partial<Pick<ChipArgs, "onChange">>) => {
    const pill = (
        <TaskStatusPill
            status={task.status}
            className={cardStyles.statusPill}
        />
    );
    return onChange === undefined ? (
        pill
    ) : (
        <TaskFieldPicker
            field="status"
            valueId={task.status}
            onChange={onChange}
            trigger={
                <TaskChipButton
                    field="status"
                    aria-label={`Status: ${TASK_STATUS_CONFIG[task.status].tooltip}`}
                >
                    {pill}
                </TaskChipButton>
            }
        />
    );
};

/**
 * The presence stack as the Assign picker's trigger: the picker's props
 * reach the assignee avatar, its button. The label and chevron the picker
 * sends a trigger are dropped.
 */
const TaskPresenceTrigger = ({
    members,
    rightIcon,
    children,
    ...buttonProps
}: ComponentPropsWithRef<"button"> & {
    members: readonly PresenceStackMember[];
    rightIcon?: ReactNode;
}) => (
    <PresenceStack
        members={members}
        assigneeButtonProps={{
            ...buttonProps,
            ...taskFieldProps("assignee"),
            tabIndex: -1,
        }}
    />
);

/**
 * The card's presence stack, bottom right. With `onChange` the assignee
 * avatar (or the empty slot) opens the Assign picker, as on the real card;
 * the other avatars open the task.
 */
export const TaskPresenceChip = ({
    task,
    members,
    onChange,
}: Pick<ChipArgs, "task"> &
    Partial<Pick<ChipArgs, "onChange">> & {
        members: readonly PresenceStackMember[];
    }) =>
    onChange === undefined ? (
        <PresenceStack members={members} />
    ) : (
        <TaskFieldPicker
            field="assignee"
            valueId={getAxisValueIds(task, "assignee")[0]}
            onChange={onChange}
            trigger={<TaskPresenceTrigger members={members} />}
        />
    );
